import { Ruler } from 'features/fde/rulers/rulerTypes'
import {
  DerivedRemoteness,
  deriveRemoteness,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRemoteness'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import {
  entryToRuler,
  rulerQueryForIndications,
} from 'features/libra/stepComponents/ffs/ffsRulerMarkup'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoComponentModel, GeckoMeasurement, RolodexEntryType } from 'types'

/**
 * Per-LTA "is this region remote from a structural discontinuity?", derived from
 * the rulers the LTA-generation backend writes (one per nearby asset-model
 * feature, tagged with the owning indication).
 *
 * Batched: one query for every LTA in the set, keyed exactly like the markup
 * hook's ruler query (`useFfsMarkupLayers`), so SWR serves both from one fetch
 * rather than doubling the round-trips.
 */

/**
 * The Eq. 5.10 inputs, both in mm.
 *
 * `D` is the OUTER diameter (`radius * 2`) where the equation wants the inner —
 * the same approximation `extractClassificationFacts` already makes, and
 * conservative here because a larger D means a larger threshold, i.e. "remote"
 * is harder to claim.
 *
 * `t_c` should be `t_rd - FCA` measured OUTSIDE the flaw, but FCA is never
 * captured at pre-planning. Nominal wall is used instead, and deliberately
 * WITHOUT a fallback to the LTA's own `ffsStats.mean`: that mean is measured
 * inside the thinned region, so it is lower than the true `t_c`, which would
 * shrink the threshold and make "remote" EASIER to claim — the unsafe
 * direction. No nominal ⇒ no derivation.
 *
 * Reads the two fields structurally, the pattern already established by
 * `computeMinThicknessValidation.ts`.
 */
function thresholdInputs(componentModel: GeckoComponentModel | undefined): {
  dMm: number | null
  tcMm: number | null
} {
  const cm = componentModel as
    | { radius?: GeckoMeasurement; wallNominalThickness?: GeckoMeasurement }
    | undefined
  // No local null/finite helper: `msdThresholdMm` already rejects anything
  // non-finite or non-positive, so guarding here would only restate it.
  const radiusMm = cm?.radius?.toNumber(WORKING_UNIT_THICKNESS)
  return {
    dMm: radiusMm === undefined ? null : radiusMm * 2,
    tcMm: cm?.wallNominalThickness?.toNumber(WORKING_UNIT_THICKNESS) ?? null,
  }
}

/**
 * Keyed by indication id. An absent id means no usable rulers came back for it,
 * which reads the same as "nothing to warn about" — deliberately no `isLoading`,
 * because nothing gates on this arriving.
 */
export type UseLtaRulerDistancesResult = ReadonlyMap<string, DerivedRemoteness>

export function useLtaRulerDistances({
  indicationIds,
  componentModel,
}: {
  indicationIds: ReadonlyArray<string>
  componentModel: GeckoComponentModel | undefined
}): UseLtaRulerDistancesResult {
  const { entries } = useRolodexEntries(
    useMemo(() => {
      const query = rulerQueryForIndications(indicationIds)
      return query ? [query] : []
    }, [indicationIds])
  )

  return useMemo(() => {
    const { dMm, tcMm } = thresholdInputs(componentModel)

    // Group by the owning indication. The parsed `Ruler` drops tags, so the
    // association has to be read off the raw entry before parsing.
    const rulersByIndication = new Map<string, Ruler[]>()
    for (const entry of entries) {
      const indicationId = getStringTag(entry.tags, RolodexEntryType.INDICATION)
      if (!indicationId) continue
      const ruler = entryToRuler(entry)
      if (!ruler) continue // malformed payload — drop it rather than guess
      const existing = rulersByIndication.get(indicationId)
      if (existing) existing.push(ruler)
      else rulersByIndication.set(indicationId, [ruler])
    }

    const byIndication = new Map<string, DerivedRemoteness>()
    for (const [indicationId, rulers] of rulersByIndication) {
      byIndication.set(indicationId, deriveRemoteness({ rulers, dMm, tcMm }))
    }
    return byIndication
  }, [componentModel, entries])
}
